import { keepPreviousData, queryOptions } from "@tanstack/react-query";
import type {
  AuditLogFilterOptionsResponse,
  AuditLogsResponse,
} from "@/app/audit-logs/types";
import { getCoordinatorApi } from "@/lib/api";
import { resolveTimeWindow } from "@/lib/date-range";
import type { OrgProjectScope } from "@/lib/resource-context";
import type { AuditLogsFilters } from "../search";
import { auditLogsKeys } from "./query-keys";

type ListParams = {
  scope: OrgProjectScope;
  filters: AuditLogsFilters;
  limit: number;
  offset: number;
};

type FilterOptionsParams = {
  scope: OrgProjectScope;
  filters: AuditLogsFilters;
};

const filterQueryParams = (filters: AuditLogsFilters) => ({
  action: filters.actions,
  source: filters.sources,
  resource_type: filters.resourceTypes,
  ...resolveTimeWindow({
    dateRange: filters.dateRange,
    startTime: filters.startTime,
    endTime: filters.endTime,
  }),
});

export const auditLogsQueryOptions = ({
  scope,
  filters,
  limit,
  offset,
}: ListParams) =>
  queryOptions<AuditLogsResponse>({
    queryKey: auditLogsKeys.list({ ...scope, filters, limit, offset }),
    queryFn: async () => {
      const { data } = await getCoordinatorApi(scope).get<AuditLogsResponse>(
        "/audit_logs",
        { params: { limit, offset, ...filterQueryParams(filters) } }
      );
      return data;
    },
    // Paging and filtering both replace the whole result set. Without this the
    // table unmounts to a skeleton on every click; with it the last page stays
    // on screen, dimmed, until the next one lands.
    placeholderData: keepPreviousData,
  });

export const auditLogFilterOptionsQueryOptions = ({
  scope,
  filters,
}: FilterOptionsParams) =>
  queryOptions<AuditLogFilterOptionsResponse>({
    queryKey: auditLogsKeys.filterOptions({ ...scope, filters }),
    queryFn: async () => {
      const { data } = await getCoordinatorApi(
        scope
      ).get<AuditLogFilterOptionsResponse>("/audit_logs/filter_options", {
        params: filterQueryParams(filters),
      });
      return data;
    },
  });
